import { Skeleton, cn } from '@heroui/react';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { AgentLogDetailsDrawer } from './components/AgentLogDetailsDrawer';
import { AgentLogsFilters } from './components/AgentLogsFilters';
import { AgentLogsTable } from './components/AgentLogsTable';
import { AgentLogsTableStats } from './components/AgentLogsTableStats';
import { AgentLogsZeroRowsView } from './components/AgentLogsZeroRowsView';
import {
  useAgentLogDrawerUrlState,
  useAgentLogDetail,
  useAgentLogsData,
  useAgentLogsFilters,
  useHasActiveFilters
} from './hooks';
import type { AgentLogsTableRow } from './types';

import i18n from '~/common/helpers/i18n';
import { useHeader } from '~/components/HeaderProvider';
import type { StoreRootState } from '~/scenes/Governance/types/StoreRootState';
import { useRetentionInfo } from '~/scenes/Monitor/scenes/PipelineLogs/hooks/useRetentionInfo';

export type AgentLogsProps = {
  embedded?: boolean;
};

export function AgentLogsPanel({ embedded = false }: AgentLogsProps) {
  const { realm, environment, environments } = useSelector(
    (state: StoreRootState) => ({
      realm: state.application?.realm.realm,
      environment: state.application?.environment,
      environments: state.application?.environments?.byName
    })
  );

  const retentionInfo = useRetentionInfo(environment, environments);
  const filters = useAgentLogsFilters();
  const { agentExecutionKey, isOpen, openDrawer, closeDrawer } =
    useAgentLogDrawerUrlState();

  const { items, loading, loadingMore, error, hasMore, handleLoadMore } =
    useAgentLogsData({
      realm,
      environment,
      queryParams: filters.queryParams,
      enabled: !!realm && !!environment
    });

  const {
    data: detail,
    isLoading: detailLoading,
    error: detailError
  } = useAgentLogDetail({
    realm,
    environment,
    agentExecutionKey,
    queryParams: filters.queryParams,
    listItems: items,
    enabled: isOpen && !!realm && !!environment
  });

  const tableData = useMemo<AgentLogsTableRow[]>(
    () => items.map(item => ({ ...item, key: item.id })),
    [items]
  );

  const hasFilters = useHasActiveFilters({
    pipelineName: filters.urlParams.pipelineName ?? '',
    agentName: filters.urlParams.agentName ?? '',
    content: filters.urlParams.content ?? '',
    time: filters.timeState.time
  });

  const handleRowClick = useCallback(
    (row: AgentLogsTableRow) => {
      openDrawer(row.agentExecutionKey);
    },
    [openDrawer]
  );

  const isRowSelected = useCallback(
    (row: AgentLogsTableRow) => row.agentExecutionKey === agentExecutionKey,
    [agentExecutionKey]
  );

  return (
    <div
      className={cn(
        'flex flex-1 flex-col overflow-auto',
        embedded ? undefined : 'p-10 pb-4'
      )}
      data-testid='agent-logs-scene'
    >
      {!embedded ? (
        <h1 className='text-xl font-semibold text-foreground'>
          {i18n.t('scenes.monitor.agentLogs.title')}
        </h1>
      ) : null}

      <div className={cn(!embedded && 'pt-5')}>
        <AgentLogsFilters
          pipelineName={filters.form.values.pipelineName}
          agentName={filters.form.values.agentName}
          content={filters.form.values.content}
          timeRange={filters.timeRange}
          resetToDefault={filters.timeState.resetToDefault}
          retentionValue={retentionInfo.value}
          onPipelineNameChange={filters.form.setters.setPipelineName}
          onAgentNameChange={filters.form.setters.setAgentName}
          onContentChange={filters.form.setters.setContent}
          onTimeRangeChange={filters.handleTimeRangeChange}
          onSearch={filters.handleSearch}
          onClear={filters.handleClear}
        />
      </div>

      <div className={cn('mt-10', embedded && 'mt-5')}>
        {error ? (
          <div
            className='text-error py-12 text-center'
            data-testid='monitor-agent-logs-message-error'
          >
            {i18n.t('scenes.monitor.agentLogs.errors.generic')}: {error.message}
          </div>
        ) : null}

        {!error ? (
          <div className='overflow-hidden'>
            {loading ? (
              <div
                className='mb-2.5 flex flex-col gap-0.5 pl-4'
                data-testid='monitor-agent-logs-skeleton-loader'
              >
                <Skeleton className='h-6 w-64 rounded' />
                <Skeleton className='h-5 w-40 rounded' />
              </div>
            ) : null}

            {!loading ? (
              <AgentLogsTableStats
                count={items.length}
                retentionValue={retentionInfo.value}
                retentionUnit={retentionInfo.unit}
              />
            ) : null}

            {!loading && tableData.length === 0 ? (
              <AgentLogsZeroRowsView
                hasFilters={hasFilters}
                onClear={filters.handleClear}
              />
            ) : (
              <AgentLogsTable
                data={tableData}
                loading={loading}
                loadingMore={loadingMore}
                hasMore={hasMore}
                onLoadMore={handleLoadMore}
                onRowClick={handleRowClick}
                isRowSelected={isRowSelected}
              />
            )}
          </div>
        ) : null}
      </div>

      <AgentLogDetailsDrawer
        isOpen={isOpen}
        realm={realm ?? ''}
        environment={environment ?? ''}
        detail={detail}
        loading={detailLoading}
        error={detailError}
        onClose={closeDrawer}
      />
    </div>
  );
}

function AgentLogsStandalone() {
  useHeader({ configActionButton: [{ hidden: true }] });

  return <AgentLogsPanel />;
}

export function AgentLogs({ embedded = false }: AgentLogsProps) {
  if (embedded) {
    return <AgentLogsPanel embedded />;
  }

  return <AgentLogsStandalone />;
}
